import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Tab, Tabs } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../../theme';
import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useDebounce } from '../Utils/useDounce';
import { Dayjs } from 'dayjs';
import {
  Person,
  Search as SearchIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { COLORS } from '../../../theme';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import { useGetSettlements } from '../../Query/SettlementsQuery';
import { useManageSettlements } from './useManageSettlements';
import { useAllMerchantforPushNotification } from '../../Query/MerchantQuery';
import { useContext } from 'react';
import { HimmaContext } from '../../../context/HimmaContext';
import { InitiateSettlementPaymentRequest, InitiateSettlementPaymentRequestPaymentModeEnum, InitiateSettlementPaymentRequestPlatformEnum } from '../../../client-sdk/api';
import { usePermissions } from '../../../context/PermissionContext';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useGetAllCustomer } from '../../Query/CustomerQuery';
import { useAllDriverManagement } from '../../Query/DriverManagerQuery';
import { uploadImage } from '../../HelperFunctions/s3Service';
import { Close as CloseIcon } from '@mui/icons-material';
import { exportToExcel, formatDataForExport } from '../../HelperFunctions/exportUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

const Settlements = () => {
  const api = useContext(HimmaContext)!;
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  
  // Check permissions for settlement actions
  const canSettle = hasPermission('settlement', 'Settle');
  const canExport = hasPermission('settlement', 'Export');
  
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  // const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
  // const [selectedAdminType, setSelectedAdminType] = useState('Admin');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [openSettleModal, setOpenSettleModal] = useState(false);
  const [activeTransaction, setActiveTransaction] = useState<any>(null);
  const [selectedTotalAmount, setSelectedTotalAmount] = useState<number>(0);
  const [selectedSettlementIds, setSelectedSettlementIds] = useState<string[]>([]);
  const [selectedModalUserType, setSelectedModalUserType] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [tempFilters, setTempFilters] = useState({
    type: '',
    status: '',
    userId: null as any,
    search: ''
  });
  const debouncedUserSearch = useDebounce(userSearch, 400);

  const { params, dispatch } = useManageSettlements();
  const [shouldFetchData, setShouldFetchData] = useState(true);
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
console.log('memoizedParams', memoizedParams);
  const { data: settlementsData, isLoading } = useGetSettlements(memoizedParams, {
    enabled: shouldFetchData && isReady
  });

  // Lazily fetch lists based on selected type
  const { data: merchantData, isLoading: merchantLoading } = useAllMerchantforPushNotification(
    { page: 1, limit: 50, sortBy: 'createdAt', sortOrder: 'desc', search: debouncedUserSearch },
    { enabled: (selectedType || '').toLowerCase() === 'admin to merchant' }
  );
  const { data: customerData, isLoading: customerLoading } = useGetAllCustomer(
    { page: 1, limit: 50, sortBy: 'name', sortOrder: 'asc', search: debouncedUserSearch } as any,
    { enabled: (selectedType || '').toLowerCase() === 'admin to customer' }
  );
  const { data: driverData, isLoading: driverLoading } = useAllDriverManagement(
    { page: 1, limit: 50, sortBy: 'createdAt', sortOrder: 'desc', search: debouncedUserSearch } as any,
    { enabled: (selectedType || '').toLowerCase() === 'admin to delivery' }
  );

  // Update API transactionType based on selected status
  useEffect(() => {
    const statusToTransactionType: Record<string, string> = {
      'toPay': 'toPay',
      'toReceive': 'toReceive', 
      'completed': 'completed',
      'refund': 'refund',
      '': 'all'
    };
    const transactionType = statusToTransactionType[selectedStatus] || 'all';
    dispatch({ type: 'SET_FILTERS', payload: { transactionType } });
    // Reset to first page on status change
    dispatch({ type: 'SET_PAGE', payload: 1 });
  }, [selectedStatus, dispatch]);

  // Update temp filters when search term changes
  useEffect(() => {
    setTempFilters(prev => ({ ...prev, search: searchTerm }));
  }, [searchTerm]);

  console.log('Settlements Data:', settlementsData);

  // Debug: Log transaction types for mapping verification
  if (settlementsData?.data?.transactions) {
    const transactionTypes = settlementsData.data.transactions.map((t: any) => t.transactionType || 'unknown');
    console.log('Transaction Types found:', [...new Set(transactionTypes)]);
    console.log('Transaction count by type:', transactionTypes.reduce((acc: any, type: any) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>));
  }

  // Use actual API data or fallback to default values


  // Calculate summary data based on transactionType from the actual transactions
  const calculateSummaryData = () => {
    const transactions = settlementsData?.data?.transactions || [];

    const toPayTransactions = transactions.filter((t: any) => (t.transactionType || '').toLowerCase() === 'topay');
    const toReceiveTransactions = transactions.filter((t: any) => (t.transactionType || '').toLowerCase() === 'toreceive');
    const completedTransactions = transactions.filter((t: any) => (t.transactionType || '').toLowerCase() === 'completed');
    const refundTransactions = transactions.filter((t: any) => (t.transactionType || '').toLowerCase() === 'refund');

    return {
      toPay: {
        amount: toPayTransactions.reduce((sum: any, t: any) => sum + (t.amount || 0), 0),
        transactions: toPayTransactions.length
      },
      toReceive: {
        amount: toReceiveTransactions.reduce((sum: any, t: any) => sum + (t.amount || 0), 0),
        transactions: toReceiveTransactions.length
      },
      completed: {
        amount: completedTransactions.reduce((sum: any, t: any) => sum + (t.amount || 0), 0),
        transactions: completedTransactions.length
      },
      refund: {
        amount: refundTransactions.reduce((sum: any, t: any) => sum + (t.amount || 0), 0),
        transactions: refundTransactions.length
      },
    };
  };
  console.log('Summary Data:', calculateSummaryData());

  // const summaryData = calculateSummaryData();

  const getFilteredTransactions = () => {
    // Ensure data.transactions exists and is an array
    const transactions = settlementsData?.data?.transactions || [];
    console.log('Raw transactions from API:', transactions);

    let filtered = [...transactions];

    // Apply status filter if selected and not 'all'
    if (selectedStatus && selectedStatus !== 'all') {
      filtered = filtered.filter(t => {
        const txnType = (t.transactionType || '').toLowerCase();
        const status = (t.status || '').toUpperCase();
        const txnCategory = (t.transactionCategory || '').toUpperCase();
        
        // Handle refund category
        if (selectedStatus === 'refund') {
          return txnCategory === 'REFUND';
        }
        
        // Handle other statuses
        if (selectedStatus === 'completed') {
          return status === 'COMPLETED' || txnType === 'completed';
        }
        
        if (selectedStatus === 'toPay') {
          return txnType === 'topay';
        }
        
        if (selectedStatus === 'toReceive') {
          return txnType === 'toreceive';
        }
        
        return false;
      });
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        (t.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.fromUser?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.toUser?.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.orderInfo?.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (t.orderInfo?.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
      );
    }

    console.log('filtered', filtered);

    return filtered;
  };

  // Memoize grid rows to maintain stable reference and allow selection syncing
  const memoizedRows = useMemo(() => {
    const filteredData = getFilteredTransactions();
    return filteredData.map((transaction, index) => ({
      ...transaction,
      id: transaction.transactionId,
      sno: index + 1,
    }));
  }, [settlementsData, selectedStatus, searchTerm, params.page, params.limit]);

  // Keep selection in sync with visible rows (avoid stale selection across filters/pages)
  const visibleRowIdSet = useMemo(() => new Set((memoizedRows || []).map((r: any) => r.id)), [memoizedRows]);
  const safeSelectedTransactions = useMemo(
    () => selectedTransactions.filter((id) => visibleRowIdSet.has(id as any)),
    [selectedTransactions, visibleRowIdSet]
  );

  // Determine if any selected row has an invalid amount (N/A)
  const hasNaAmountInSelection = useMemo(() => {
    const selected = (memoizedRows || []).filter((row: any) => row.id && safeSelectedTransactions.includes(row.id));
    return selected.some((row: any) => {
      const amount = row?.amount;
      if (amount === null || amount === undefined) return true;
      if (typeof amount === 'string' && amount.toUpperCase() === 'N/A') return true;
      return false;
    });
  }, [memoizedRows, safeSelectedTransactions]);

  // Clear selection whenever filters/page/data change to prevent accidental enabled state
  useEffect(() => {
    // If any selected ids are no longer visible, drop them
    if (selectedTransactions.length && safeSelectedTransactions.length !== selectedTransactions.length) {
      setSelectedTransactions(safeSelectedTransactions as any);
      return;
    }
    if (!selectedTransactions.length) return;
    setSelectedTransactions([]);
  }, [selectedStatus, searchTerm, params.page, params.limit, settlementsData]);


  // const handleAdminClick = (event: React.MouseEvent<HTMLElement>) => {
  //   setAdminAnchorEl(event.currentTarget);
  // };

  // const handleAdminClose = () => {
  //   setAdminAnchorEl(null);
  // };

  // const handleAdminTypeSelect = (type: string) => {
  //   setSelectedAdminType(type);
  //   setAdminAnchorEl(null);
  // };

  const handleFromDateChange = (newValue: Dayjs | null) => {
    setSelectedFromDate(newValue);
    // Dispatch the from date to the useManageSettlements hook
    const dateString = newValue ? newValue.toISOString().split('T')[0] + 'T00:00:00Z' : undefined;
    console.log('Selected From Date:', dateString);
    dispatch({ type: 'SET_FROM_DATE', payload: dateString });
  };

  const handleToDateChange = (newValue: Dayjs | null) => {
    setSelectedToDate(newValue);
    // Dispatch the to date to the useManageSettlements hook
    const dateString = newValue ? newValue.toISOString().split('T')[0] + 'T23:59:59Z' : undefined;
    console.log('Selected To Date:', dateString);
    dispatch({ type: 'SET_TO_DATE', payload: dateString });
  };

  const handleTypeChange = (event: any) => {
    const newType = event.target.value;
    setSelectedType(newType);
    // Reset user on type change
    setSelectedUser(null);
    setTempFilters(prev => ({ ...prev, type: newType, userId: null }));
  };

  const handleStatusChange = (event: any) => {
    const newStatus = event.target.value;
    setSelectedStatus(newStatus);
    setTempFilters(prev => ({ ...prev, status: newStatus }));
  };

  // Update user options when type or datasets change
  useEffect(() => {
    let options: { value: string; label: string }[] = [];
    const type = (selectedType || '').toLowerCase();
    if (type === 'admin to merchant') {
      const list = merchantData?.data ?? [];
      options = list
        .map((m: any) => ({
          value: m.id || m.merchantId || m.userId,
          label: (m.firstName || m.name || m.merchantName || m.fullName || '').toString().trim() +
                 (m.lastName ? ` ${m.lastName}` : ''),
        }))
        .filter((o: any) => o.value && o.label && o.label.trim() !== '');
    } else if (type === 'admin to customer') {
      const list = customerData?.data ?? [];
      options = list
        .map((c: any) => ({ value: c.userId || c.id, label: c.name || c.fullName }))
        .filter((o: any) => o.value && o.label);
    } else if (type === 'admin to delivery') {
      const list = driverData?.data ?? [];
      options = list
        .map((d: any) => ({ value: d.deliverBoyId || d.deliveryBoyId || d.id, label: `${d.firstName || ''} ${d.lastName || ''}`.trim() }))
        .filter((o: any) => o.value && o.label);
    } else {
      options = [];
    }
    setUserOptions(options);
  }, [selectedType, merchantData, customerData, driverData]);

  // When type changes, clear user search once to avoid clearing after data refetches
  useEffect(() => {
    setUserSearch('');
  }, [selectedType]);

  // Map UI type values to API expected values
  const mapUserTypeToApiValue = (uiType: string): string | undefined => {
    if (!uiType) return undefined;
    
    const typeMapping: Record<string, string> = {
      'admin to merchant': 'MERCHANT',
      'admin to customer': 'CUSTOMER',
      'admin to delivery': 'DELIVERY_PARTNER'
    };
    
    return typeMapping[uiType.toLowerCase()] || undefined;
  };

  // Apply filters to API
  const handleApplyFilters = () => {
    // Apply all temporary filters to the API with proper mapping
    dispatch({ type: 'SET_FILTERS', payload: { 
      settlementUserType: mapUserTypeToApiValue(tempFilters.type),
      settlementUserId: tempFilters.userId?.value || undefined,
      transactionType: tempFilters.status || undefined
    }});
    dispatch({ type: 'SET_SEARCH', payload: tempFilters.search?.trim() || undefined });
    dispatch({ type: 'SET_PAGE', payload: 1 });
    // Enable API call when Apply button is clicked
    setShouldFetchData(true);
  };

  // Reset all filters including dates
  const handleResetFilters = () => {
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setSearchTerm('');
    setSelectedType('');
    setSelectedStatus('');
    setSelectedUser(null);
    setTempFilters({
      type: '',
      status: '',
      userId: null,
      search: ''
    });
    dispatch({ type: 'RESET' });
    // Keep API call enabled after reset to show default data
    setShouldFetchData(true);
  };

  // Check if both dates are selected
  const bothDatesSelected = selectedFromDate && selectedToDate;

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
    // Ensure API call is enabled for pagination
    if (!shouldFetchData) {
      setShouldFetchData(true);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    // Update limit and reset to first page
    dispatch({ type: 'SET_LIMIT', payload: newSize });
    dispatch({ type: 'SET_PAGE', payload: 1 });
    // Ensure API call is enabled for page size change
    if (!shouldFetchData) {
      setShouldFetchData(true);
    }
  };

  // const getStatusChip = (status: string, date: string) => {
  //   const statusConfig: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'error'; icon: React.ReactElement }> = {
  //     'PENDING': { label: 'Pending', color: 'warning', icon: <Schedule /> },
  //     'IN_PROGRESS': { label: 'In Progress', color: 'info', icon: <Schedule /> },
  //     'COMPLETED': { label: 'Completed', color: 'success', icon: <CheckCircle /> },
  //     'FAILED': { label: 'Failed', color: 'error', icon: <Schedule /> },
  //   };

  //   const config = statusConfig[status] || statusConfig['PENDING'];
  //   const formattedDate = new Date(date).toLocaleDateString();

  //   return (
  //     <Box display="flex" alignItems="center" gap={1}>
  //       <Chip
  //         label={config.label}
  //         size="small"
  //         color={config.color}
  //         icon={config.icon}
  //       />
  //       <Typography variant="caption" color="text.secondary">
  //         {formattedDate}
  //       </Typography>
  //     </Box>
  //   );
  // };


  // Prepare data for grid based on filters
  const getGridData = () => {
    console.log('Final grid data:', memoizedRows);
    return memoizedRows as any;
  };

  // Get columns based on selected status or default to all columns
  const getColumns = () => {
    // Use a unified column set that works for all transaction types
    const unifiedColumns: GridColDef[] = [
      {
        field: 'sno',
        headerName: 'S.No',
        flex: 0.5,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => params.row.sno,
      },
      {
        field: 'order Number',
        headerName: 'Order Number',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          // Use orderInfo.orderNumber if available (more user-friendly), otherwise use orderId
          const orderNumber = params.row.orderNumber;
          return orderNumber || 'N/A';
        },
      },
      {
        field: 'fromUserType',
        headerName: 'From User Type',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.row.fromUserType || 'N/A',
      },
      {
        field: 'toUserType',
        headerName: 'To User Type',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.row.toUserType || 'N/A',
      },
      {
        field: 'transactionCategory',
        headerName: 'Transaction Category',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.row.transactionCategory || 'N/A',
      },
      {
        field: 'amount',
        headerName: 'Amount (JOD)',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => params.row.amount || 'N/A',
      },
      {
        field: 'transactionType',
        headerName: 'Type',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.row.transactionType || 'N/A',
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.row.status || 'N/A',
      },
    ];
    
    return unifiedColumns;
  };

  // Handle bulk actions button click
  const handleBulkActions = () => {
    if (selectedTransactions.length === 0) return;
    
    // Get the selected transaction data
    const selectedTransactionData = getGridData().filter((row: any) => 
      row.id && selectedTransactions.includes(row.id)
    );
    
    // For now, open the settle modal with the first selected transaction
    // You can modify this logic based on your requirements
    if (selectedTransactionData.length > 0) {
      setActiveTransaction(selectedTransactionData[0]);
      // Compute total amount and selected settlement IDs
      const totalAmount = selectedTransactionData.reduce((sum: number, row: any) => {
        const amount = typeof row.amount === 'string' ? parseFloat(row.amount) : row.amount;
        return sum + (Number.isFinite(amount) ? Number(amount) : 0);
      }, 0);
      setSelectedTotalAmount(totalAmount);
      setSelectedSettlementIds(selectedTransactionData.map((row: any) => row.transactionId).filter(Boolean));
      // Determine user type for modal (prefer CUSTOMER if any selected is CUSTOMER)
      const hasCustomer = selectedTransactionData.some((row: any) => (row.toUserType || row.fromUserType) === 'CUSTOMER');
      const fallbackType = selectedTransactionData[0]?.toUserType || selectedTransactionData[0]?.fromUserType || null;
      setSelectedModalUserType(hasCustomer ? 'CUSTOMER' : fallbackType);
      setOpenSettleModal(true);
    }
  };

  // Get action button text based on selected status
  // const getActionButtonText = () => {
  //   switch (selectedStatus) {
  //     case 'toPay':
  //       return `Settle Payment (${safeSelectedTransactions.length})`;
  //     case 'toReceive':
  //       return `Receive Payment (${safeSelectedTransactions.length})`;
  //     case 'completed':
  //       return `View Details (${safeSelectedTransactions.length})`;
  //     case 'refund':
  //       return `Refund Details (${safeSelectedTransactions.length})`;
  //     case 'all':
  //       return `Bulk Actions (${safeSelectedTransactions.length})`;
  //     default:
  //       return `Bulk Actions (${safeSelectedTransactions.length})`;
  //   }
  // };

  // Get action button color based on selected status
  const getActionButtonColor = () => {
    switch (selectedStatus) {
      case 'toPay':
        return 'linear-gradient(135deg, #d32f2f 0%, #ef5350 100%)';
      case 'toReceive':
        return 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)';
      case 'completed':
        return 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)';
      case 'refund':
        return 'linear-gradient(135deg, #d32f2f 0%, #ef5350 100%)';
      default:
        return 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
    }
  };

  // Get action button hover color based on selected status
  const getActionButtonHoverColor = () => {
    switch (selectedStatus) {
      case 'toPay':
        return 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)';
      case 'toReceive':
        return 'linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)';
      case 'completed':
        return 'linear-gradient(135deg, #01579b 0%, #03a9f4 100%)';
      case 'refund':
        return 'linear-gradient(135deg, #b71c1c 0%, #ef5350 100%)';
      default:
        return 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)';
    }
  };

  // Export Excel function
  const handleExportExcel = () => {
    try {
      const filteredData = getFilteredTransactions();
      const formattedData = formatDataForExport(filteredData);
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `settlements_${timestamp}`;
      
      const result = exportToExcel(formattedData, filename);
      if (result.success) {
        showMessage('Excel file exported successfully!', 'success');
      } else {
        showMessage(result.message, 'error');
      }
    } catch (error) {
      console.error('Export Excel error:', error);
      showMessage('Failed to export Excel file', 'error');
    }
  };

  // Export PDF function
  const handleExportPDF = () => {
    try {
      const filteredData = getFilteredTransactions();
      
      // Create new PDF document
      const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation for better table fit
      
      // Add title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Settlements Report', 14, 15);
      
      // Add date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25);
      
      // Add summary information
      doc.text(`Total Records: ${filteredData.length}`, 14, 35);
      
      // Prepare table data
      const tableData = filteredData.map((transaction, index) => [
        index + 1,
        transaction.orderNumber || 'N/A',
        transaction.fromUser?.userType || 'N/A',
        transaction.toUser?.userType || 'N/A',
        transaction.transactionCategory || 'N/A',
        transaction.amount || 0,
        transaction.transactionType || 'N/A',
        transaction.status || 'N/A',
      ]);


      // Add table using autoTable plugin
      autoTable(doc, {
        head: [['S.No', 'Order Number', 'From User', 'To User', 'Transaction Category', 'Amount (JOD)', 'Type', 'Status']],
        body: tableData,
        startY: 45,
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [139, 69, 19], // Brown color matching the theme
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 15 }, // S.No
          1: { cellWidth: 35 }, // Order Number
          2: { cellWidth: 25 }, // From User
          3: { cellWidth: 25 }, // To User
          4: { cellWidth: 35 }, // Transaction Category
          5: { halign: 'center', cellWidth: 20 }, // Amount
          6: { cellWidth: 25 }, // Type
          7: { cellWidth: 25 }, // Status
        },
      });

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Settlements_${currentDate}.pdf`;

      // Save file
      doc.save(filename);
      
      showMessage('PDF file exported successfully!', 'success');
    } catch (error) {
      console.error('Export PDF error:', error);
      showMessage('Failed to export PDF file', 'error');
    }
  };

  // Remove full page loading - let the grid handle its own loading state

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold" color={COLORS.secondary.main}>
          Settlements
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              backgroundColor: COLORS.primary.main,
              color: COLORS.secondary.main,
              borderRadius: 1,
              fontWeight: 'bold',
            }}
          >
            <Person />
            Admin
          </Box>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Person />}
          endIcon={<KeyboardArrowDown />}
          onClick={handleAdminClick}
          sx={{
            backgroundColor: 'rgb(76, 10, 39)',
            '&:hover': {
              backgroundColor: '#5a0f2e',
            }
          }}
        >
          {selectedAdminType}
        </Button>
        <Menu
          anchorEl={adminAnchorEl}
          open={Boolean(adminAnchorEl)}
          onClose={handleAdminClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 150,
              borderRadius: 2,
              boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #dddddd',
              '& .MuiMenuItem-root': {
                py: 1.5,
                px: 2,
                fontSize: '14px',
                fontWeight: 500,
                color: '#30333a',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                '&:first-of-type': {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                },
                '&:last-of-type': {
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                },
              },
            },
          }}
        >
          <MenuItem onClick={() => handleAdminTypeSelect('Customer')}>
            Customer
          </MenuItem>
          <MenuItem onClick={() => handleAdminTypeSelect('Driver')}>
            Driver
          </MenuItem>
          <MenuItem onClick={() => handleAdminTypeSelect('Merchant')}>
            Merchant
          </MenuItem>
        </Menu> */}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {/* Row 1: Type + User + Status */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography>Type</Typography>
          <Box sx={{ width: '250px' }}>
            <FormControl fullWidth size="small">
              <Select
                value={selectedType}
                onChange={handleTypeChange}
                displayEmpty
                sx={{
                  width: '250px',
                  '& .MuiInputBase-root': {
                    width: '179px',
                    marginTop: '12px',
                    height: 42,
                    display: 'flex',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '100%',
                    padding: '0 14px',
                    boxSizing: 'border-box',
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select Type</em>
                </MenuItem>
                <MenuItem value="admin to merchant">Merchant</MenuItem>
                <MenuItem value="admin to customer">Customer</MenuItem>
                <MenuItem value="admin to delivery">Delivery</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography>User</Typography>
          <Box sx={{ width: '250px' }}>
            <Autocomplete
              options={useMemo(() => {
                const input = (userSearch || '').toLowerCase();
                if (!input) return userOptions;
                return userOptions.filter((o) => (o.label || '').toLowerCase().includes(input));
              }, [userOptions, userSearch])}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={selectedUser}
              inputValue={userSearch}
              onInputChange={(_, val) => setUserSearch(val)}
              onChange={(_, newValue) => {
                setSelectedUser(newValue);
                setTempFilters(prev => ({ ...prev, userId: newValue }));
              }}
              loading={merchantLoading || customerLoading || driverLoading}
              disabled={!selectedType}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedType ? 'Select User' : 'Select Type first'}
                  size="small"
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root': {
                      width: '250px',
                      marginTop: '12px',
                      height: 42,
                      display: 'flex',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '100%',
                      padding: '0 14px',
                      boxSizing: 'border-box',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(merchantLoading || customerLoading || driverLoading) ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Typography>Status</Typography>
          <Box sx={{ width: '250px' }}>
            <FormControl fullWidth size="small">
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                displayEmpty
                sx={{
                  width: '250px',
                  '& .MuiInputBase-root': {
                    width: '179px',
                    marginTop: '12px',
                    height: 42,
                    display: 'flex',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    height: '100%',
                    padding: '0 14px',
                    boxSizing: 'border-box',
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Status</em>
                </MenuItem>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="toPay">To Pay</MenuItem>
                <MenuItem value="toReceive">To Receive</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Row 2: Date filters + Reset */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body1" color={bothDatesSelected ? 'text.primary' : 'text.secondary'}>
            Filter by From date:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedFromDate}
              onChange={handleFromDateChange}
              slotProps={{
                textField: {
                  placeholder: "Pick from date",
                  sx: { minWidth: 200 }
                }
              }}
            />
          </LocalizationProvider>

          <Typography variant="body1" color={bothDatesSelected ? 'text.primary' : 'text.secondary'}>
            To date:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedToDate}
              onChange={handleToDateChange}
              slotProps={{
                textField: {
                  placeholder: "Pick to date",
                  sx: { minWidth: 200 }
                }
              }}
            />
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              backgroundColor: COLORS.primary.main,
              color: COLORS.secondary.main,
              minWidth: 100,
              height: 40,
              fontWeight: 'bold',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: COLORS.primary.dark,
              },
            }}
          >
            Apply
          </Button>

          <Button
            variant="outlined"
            onClick={handleResetFilters}
            sx={{
              borderColor: '#8B4513',
              color: '#8B4513',
              backgroundColor: 'white',
              minWidth: 100,
              height: 40,
              fontWeight: 'normal',
              borderRadius: 2,
              '&:hover': {
                borderColor: '#A0522D',
                backgroundColor: 'rgba(139, 69, 19, 0.04)',
              },
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* Date Selection Helper */}
      {!bothDatesSelected && (selectedFromDate || selectedToDate) && (
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', color: 'red' }}>
            Please select both "From date" and "To date" to filter the data
          </Typography>
        </Box>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #d32f2f 0%, #ef5350 100%)',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent>
            <Typography variant="h6" mb={2}>To Pay</Typography>
            <Typography variant="h3" fontWeight="bold" mb={1}>
              JOD {settlementsData?.data?.summary?.totalAmountToPay?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2">
              {settlementsData?.data?.summary?.countToPay || 0} transactions
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent>
            <Typography variant="h6" mb={2}>To Receive</Typography>
            <Typography variant="h3" fontWeight="bold" mb={1}>
              JOD {settlementsData?.data?.summary?.totalAmountToReceive?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2">
              {settlementsData?.data?.summary?.countToReceive || 0} transactions
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)',
            color: 'white',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent>
            <Typography variant="h6" mb={2}>Completed</Typography>
            <Typography variant="h3" fontWeight="bold" mb={1}>
              JOD {settlementsData?.data?.summary?.totalAmountCompleted?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2">
              {settlementsData?.data?.summary?.countCompleted || 0} transactions
            </Typography>
          </CardContent>
        </Card>
      </Box>


      {/* Content - Grid Layout for all transactions */}
      <Box sx={{ p: 2, border: '1px solid #e0e0e0', marginTop: '20px', color: '#666' }}>
        {/* Search and Action Buttons */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* Search */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by Order ID, store name, or user names"
              size="medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleApplyFilters();
                }
              }}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none',
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {/* Export Buttons */}
            {canExport && (
              <Button
                variant="outlined"
                startIcon={<ExcelIcon />}
                onClick={handleExportExcel}
                disabled={!settlementsData?.data?.transactions?.length}
              sx={{
                borderColor: '#2e7d32',
                color: '#2e7d32',
                minWidth: 120,
                height: 40,
                fontWeight: 'bold',
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#1b5e20',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)',
                },
                '&:disabled': {
                  borderColor: '#ccc',
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
                Export Excel
              </Button>
            )}

            {canExport && (
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                disabled={!settlementsData?.data?.transactions?.length}
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                minWidth: 120,
                height: 40,
                fontWeight: 'bold',
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
                '&:disabled': {
                  borderColor: '#ccc',
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
                Export PDF
              </Button>
            )}

            {selectedStatus === 'toPay' && canSettle && (
              <Button
                variant="contained"
                disabled={safeSelectedTransactions.length === 0 || hasNaAmountInSelection}
                onClick={handleBulkActions}
                sx={{
                  background: safeSelectedTransactions.length > 0
                    && !hasNaAmountInSelection ? getActionButtonColor()
                    : '#94a3b8',
                  color: 'white',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  minWidth: 150,
                  boxShadow: safeSelectedTransactions.length > 0 && !hasNaAmountInSelection
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  '&:hover': {
                    background: safeSelectedTransactions.length > 0 && !hasNaAmountInSelection
                      ? getActionButtonHoverColor()
                      : '#94a3b8',
                    boxShadow: safeSelectedTransactions.length > 0 && !hasNaAmountInSelection
                      ? '0 6px 8px -1px rgba(0, 0, 0, 0.15)'
                      : 'none',
                  },
                }}
              >
                {`Settle Payment (${safeSelectedTransactions.length})`}
              </Button>
            )}
          </Box>
        </Box>

        {/* Data Grid */}
        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={getGridData()}
          columns={getColumns()}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={isLoading}
          checkboxSelection={selectedStatus !== 'completed' && canSettle}
          isRowSelectable={(params: any) => {
            // If user doesn't have settle permission, don't allow selection
            if (!canSettle) return false;
            // Prevent selecting completed rows regardless of filter
            const row = params?.row || {};
            const type = (row.transactionType || '').toLowerCase();
            const amt = row?.amount;
            const isNaAmount = amt === null || amt === undefined || (typeof amt === 'string' && (amt || '').toUpperCase() === 'N/A');
            return type !== 'completed' && !isNaAmount;
          }}
          selectionModel={safeSelectedTransactions}
          onRowSelectionModelChange={(model: any) => setSelectedTransactions(model as any)}
          components={{
            NoRowsOverlay: () => (
              <ErrorHandler 
                type="no-data" 
                message={!shouldFetchData ? "Click 'Apply' to load settlements data" : "No transactions found"} 
                onRetry={() => { }} 
              />
            )
          }}
          rowCount={settlementsData?.data?.pagination?.totalItems || 0}
          page={params.page - 1}
        />
      </Box>

      {/* Settle Modal */}
      <Dialog open={openSettleModal} onClose={() => {
        setOpenSettleModal(false);
        setPreviewUrl(null);
        setTabIndex(0);
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: COLORS.primary.main, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Settle Payment
          <IconButton
            onClick={() => {
              setOpenSettleModal(false);
              setPreviewUrl(null);
              setTabIndex(0);
              setSelectedTotalAmount(0);
              setSelectedSettlementIds([]);
              setSelectedModalUserType(null);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <ThemeProvider theme={theme}>
            {AllForm({
              initialValues: {
                transactionId: '',
                // Default to wallet for customers, manual otherwise
                paymentMode: (selectedModalUserType === 'CUSTOMER') ? 'wallet' : 'manual',
                amount: selectedTotalAmount || activeTransaction?.amount || '',
                remarks: '',
                attachment: null,
              },
              validationSchema: Yup.object({
                transactionId: Yup.string().required('Transaction ID is required'),
                paymentMode: Yup.string().required('Payment mode is required'),
                amount: Yup.number().typeError('Enter a valid amount').positive('Must be positive').required('Amount is required'),
                // Relaxed validations per request
                remarks: Yup.string().nullable(),
                attachment: Yup.string().nullable(),
              }),
              onSubmit: async (values: any) => {
                if (isSubmitting) return; // Prevent multiple submissions
                
                setIsSubmitting(true);
                try {
                  // Get userId from localStorage
                  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                  const userId = userData?.userId;
                  
                  if (!userId) {
                    console.error('User ID not found in localStorage');
                    showMessage('User ID not found. Please login again.', 'error');
                    return;
                  }

                  // Map payment mode to API enum
                  const paymentModeMapping = {
                    'wallet': InitiateSettlementPaymentRequestPaymentModeEnum.Wallet,
                    'manual': InitiateSettlementPaymentRequestPaymentModeEnum.Manual,
                    'online': InitiateSettlementPaymentRequestPaymentModeEnum.Online
                  };

                  // Prepare API request
                  const apiRequest: InitiateSettlementPaymentRequest = {
                    userId: userId,
                    settlementIds: (selectedSettlementIds.length ? selectedSettlementIds : [activeTransaction?.transactionId]).filter(Boolean) as any,
                    paymentMode: paymentModeMapping[values.paymentMode as keyof typeof paymentModeMapping],
                    amount: Number(values.amount),
                    remarks: values.remarks,
                    platform: InitiateSettlementPaymentRequestPlatformEnum.Web, // Set platform as web
                    // Add external transaction ID for manual payments
                    ...(values.paymentMode === 'manual' && { externalTransactionId: values.transactionId }),
                    // Add transaction ID and payment proof
                    ...(values.transactionId && { transactionId: values.transactionId }),
                    ...(values.attachment && { paymentProof: values.attachment })
                  };

                  console.log('Settlement API Request:', apiRequest);
                  console.log('Request structure includes:', {
                    platform: apiRequest.platform,
                    transactionId: apiRequest.transactionId,
                    paymentProof: apiRequest.paymentProof,
                    userId: apiRequest.userId,
                    paymentMode: apiRequest.paymentMode
                  });
                  console.log('File attachment details:', {
                    attachment: values.attachment,
                    attachmentType: typeof values.attachment,
                    paymentProof: values.attachment
                  });

                  // Call the API
                  const response = await api.initiateSettlementPayment(apiRequest);
                  
                  console.log('Settlement API Response:', response);
                  
                  // Handle success
                  if (response.data) {
                    console.log('Settlement payment initiated successfully:', response.data);
                    showMessage('Settlement payment initiated successfully!', 'success');
                    // Close modal
                    setOpenSettleModal(false);
                    // Refresh the page after a short delay to ensure message is shown
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  }
                  
                } catch (error: any) {
                  console.error('Error initiating settlement payment:', error);
                  const errorMessage = error?.response?.data?.message || error?.message || 'Failed to initiate settlement payment';
                  showMessage(errorMessage, 'error');
                } finally {
                  setIsSubmitting(false);
                }
              },
              fields: [
                {
                  name: 'paymentMode',
                  label: 'Payment Mode',
                  type: 'radio',
                  radioOptions: (() => {
                    const isCustomer = selectedModalUserType === 'CUSTOMER';
                    return isCustomer ? ['wallet', 'manual'] : ['manual'];
                  })(),
                  isRequired: true,
                },
                { 
                  name: 'transactionId', 
                  label: 'Transaction ID', 
                  type: 'text', 
                  isRequired: true,
                  placeholder: 'Enter Transaction ID'
                },
                { 
                  name: 'amount', 
                  label: 'Amount (JOD)', 
                  type: 'number', 
                  isRequired: true,
                  placeholder: 'Enter Amount'
                },
                { 
                  name: 'remarks', 
                  label: 'Remarks', 
                  type: 'text', 
                  isRequired: false,
                  placeholder: 'Enter Remarks'
                },
                {
                  name: 'attachment',
                  label: 'Payment Proof',
                  type: 'file',
                  isRequired: false,
                  component: ({ field, form }: any) => {
                    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          // Upload file to S3
                          const uploadResult = await uploadImage(file);
                          form.setFieldValue(field.name, uploadResult.url);
                          form.setFieldTouched(field.name, true, false);
                          form.setFieldError(field.name, undefined);
                          setPreviewUrl(uploadResult.url);
                        } catch (error) {
                          console.error('File upload error:', error);
                          form.setFieldError(field.name, 'Failed to upload file');
                        }
                      }
                    };

                    const handleClear = () => {
                      form.setFieldValue(field.name, null);
                      form.setFieldError(field.name, undefined);
                      setPreviewUrl(null);
                      // Reset file input
                      const fileInput = document.getElementById(`${field.name}-file-input`) as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    };

                    return (
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Button
                            variant="outlined"
                            component="label"
                      sx={{
                              height: '40px',
                              minWidth: '140px',
                              textTransform: 'none',
                              borderColor: COLORS.primary.main,
                              color: COLORS.primary.main,
                              '&:hover': {
                                borderColor: COLORS.primary.dark,
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              }
                            }}
                          >
                            Choose File
                            <input
                              id={`${field.name}-file-input`}
                              type="file"
                              accept="image/*,.pdf,.doc,.docx"
                              onChange={handleFileChange}
                              style={{ display: 'none' }}
                            />
                          </Button>
                          
                          {field.value && (
                            <>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary', 
                                  flex: 1,
                                  fontSize: '0.875rem',
                                  fontWeight: 400
                                }}
                              >
                                File selected
                              </Typography>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleClear}
                                sx={{
                                  height: '40px',
                                  minWidth: '70px',
                                  textTransform: 'none',
                                  fontSize: '0.875rem',
                                  borderColor: COLORS.error?.main || '#d32f2f',
                                  color: COLORS.error?.main || '#d32f2f',
                                  '&:hover': {
                                    borderColor: COLORS.error?.dark || '#b71c1c',
                                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                  }
                                }}
                              >
                                Clear
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setPreviewOpen(true)}
                                sx={{
                                  height: '40px',
                                  minWidth: '70px',
                                  textTransform: 'none',
                                  fontSize: '0.875rem',
                                  borderColor: COLORS.primary.main,
                                  color: COLORS.primary.main,
                                  '&:hover': {
                                    borderColor: COLORS.primary.dark,
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  }
                                }}
                              >
                                Preview
                              </Button>
                            </>
                          )}
                        </Box>
                        
                        {!field.value && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary', 
                              mb: 1,
                              fontSize: '0.875rem',
                              fontStyle: 'italic'
                            }}
                          >
                            No file chosen
                          </Typography>
                        )}
                        
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary', 
                            display: 'block',
                            fontSize: '0.75rem',
                            lineHeight: 1.4
                          }}
                        >
                          Upload payment proof document (invoice, screenshot, etc.)
                        </Typography>
                      </Box>
                    );
                  },
                },
              ],
              customButton: (formikProps: any) => (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setOpenSettleModal(false);
                      setPreviewUrl(null);
                      setTabIndex(0);
                    }}
                    sx={{ 
                      textTransform: 'none',
                      minWidth: '100px',
                      height: '40px'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      Object.keys(formikProps.values).forEach((name) => formikProps.setFieldTouched(name, true, false));
                      if (Object.keys(formikProps.errors).length === 0) {
                        formikProps.handleSubmit();
                      }
                    }}
                    sx={{ 
                      textTransform: 'none',
                      minWidth: '100px',
                      height: '40px',
                      backgroundColor: COLORS.primary.main,
                      '&:hover': {
                        backgroundColor: COLORS.primary.dark,
                      }
                    }}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
                  >
                    {isSubmitting ? 'Processing...' : 'Submit'}
                  </Button>
                </Box>
              ),
            })()}
          </ThemeProvider>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: COLORS.primary.main, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Preview Payment Proof
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
            <Tab label="Mobile View" />
            <Tab label="Web View" />
          </Tabs>

          {!previewUrl ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No file to preview
              </Typography>
            </Box>
          ) : (
            <Box>
              {tabIndex === 0 && (
                <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                  <img
                    src={previewUrl}
                    alt="Mobile Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                    }}
                  />
                </Box>
              )}
              {tabIndex === 1 && (
                <Box sx={{ width: '100%' }}>
                  <img
                    src={previewUrl}
                    alt="Web Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Settlements;